import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRecordPlacementMutation, PlacementInput } from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast/useToasts';
import { careerErrorMessage } from './careerModel';

interface IRecordPlacementHook {
  recordPlacement: (
    memberId: string,
    input: PlacementInput,
  ) => Promise<boolean>;
  pending: boolean;
}

/**
 * Spec 0018 increment B — record a graduate placement from the Дашборд tab
 * (`recordPlacement`). This flips a member to PLACED backend-side and is the
 * centre's KPI, so the dashboard funnel is refetched via `onRecorded`.
 */
export const useRecordPlacement = (
  onRecorded?: () => void,
): IRecordPlacementHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [run] = useRecordPlacementMutation();
  const [pending, setPending] = useState(false);

  const recordPlacement: IRecordPlacementHook['recordPlacement'] = useCallback(
    async (memberId, input) => {
      setPending(true);
      try {
        const result = await run({ variables: { memberId, input } });
        const payload = result.data?.recordPlacement;
        if (payload?.success) {
          onRecorded?.();
          addToast({
            type: GlobalToastTypeNames.CareerCenter,
            data: {
              type: ToastTypes.success,
              title: t('pages.careerCenter.dashboard.placement.toastSuccess'),
            },
          });
          return true;
        }
        addToast({
          type: GlobalToastTypeNames.CareerCenter,
          data: {
            type: ToastTypes.error,
            title: t('pages.careerCenter.dashboard.placement.toastError'),
            text: careerErrorMessage(t, payload?.customErrors),
          },
        });
        return false;
      } catch (e) {
        addToast({
          type: GlobalToastTypeNames.CareerCenter,
          data: {
            type: ToastTypes.error,
            title: t('pages.careerCenter.dashboard.placement.toastError'),
            text: (e as Error).message,
          },
        });
        return false;
      } finally {
        setPending(false);
      }
    },
    [run, onRecorded, addToast, t],
  );

  return { recordPlacement, pending };
};
